import axios from 'axios';

const stringifyPayload = (payload: unknown): string => {
  if (payload == null) return '';
  if (typeof payload === 'string') return payload;
  if (typeof payload === 'number' || typeof payload === 'boolean') return String(payload);

  if (Array.isArray(payload)) {
    return payload.map((item) => stringifyPayload(item)).filter(Boolean).join('; ');
  }

  if (typeof payload === 'object') {
    const record = payload as Record<string, unknown>;

    // The bridge reports failures as { error: <code>, message: <human reason> }
    // (often wrapped in a `detail`). Surface the human `message` — prefixed by
    // the code when present — so the real reason shows instead of only the code.
    const codeText = stringifyPayload(record.error);
    const messageText = stringifyPayload(record.message);
    if (messageText) return codeText ? `${codeText}: ${messageText}` : messageText;

    const preferred = record.error ?? record.detail ?? record.error_summary;
    if (preferred != null) {
      const preferredText = stringifyPayload(preferred);
      if (preferredText) return preferredText;
    }

    try {
      return JSON.stringify(payload);
    } catch {
      return String(payload);
    }
  }

  return String(payload);
};

export const formatAxiosError = (error: unknown, fallback = 'Request failed'): string => {
  const record = error as { isAxiosError?: boolean; response?: { status?: number; statusText?: string; data?: unknown }; message?: string; code?: string } | undefined;

  if (axios.isAxiosError(error) || record?.isAxiosError || record?.response) {
    const status = record?.response?.status;
    const statusText = record?.response?.statusText;
    const payloadText = stringifyPayload(record?.response?.data);
    const message = record?.message && record.message !== 'Request failed' ? record.message : '';

    const parts: string[] = [];
    if (status) {
      parts.push(`HTTP ${status}${statusText ? ` ${statusText}` : ''}`);
    }
    if (payloadText) parts.push(payloadText);
    if (!payloadText && message) parts.push(message);
    if (record?.code) parts.push(`code=${record.code}`);

    return parts.length > 0 ? parts.join(': ') : fallback;
  }

  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error) return error;
  return fallback;
};

/**
 * Extracts the raw server-reported message for a failed save, without the
 * `HTTP <status>: ` prefix formatAxiosError prepends — for toasts that quote a
 * validation error inline (e.g. "Failed to create pipeline: checkpoint[2]
 * (generate-tree-image): generator.provider is required (…)") where the status
 * code adds noise instead of clarity. Falls back to the axios/Error message.
 */
export const formatValidationError = (error: unknown, fallback = 'Request failed'): string => {
  const record = error as { response?: { data?: unknown }; message?: string } | undefined;
  const data = record?.response?.data;
  if (data != null && typeof data === 'object' && !Array.isArray(data)) {
    const payload = data as Record<string, unknown>;
    const message = payload.error ?? payload.message;
    if (typeof message === 'string' && message.trim()) return message;
  } else if (typeof data === 'string' && data.trim()) {
    return data;
  }

  if (typeof record?.message === 'string' && record.message.trim()) return record.message;
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error) return error;
  return fallback;
};

/**
 * Extracts the { approved, required } counts the bridge reports when LoRA
 * training is refused for too few approved candidates. The bridge detail reads
 * "approved candidate count X is below required minimum Y" (optionally wrapped
 * by an error code). Returns null when the error isn't that case so callers fall
 * back to the generic formatted message.
 */
export const parseApprovedCandidateShortfall = (
  error: unknown
): { approved: number; required: number } | null => {
  const text = formatAxiosError(error, '');
  if (!text) return null;
  // Tolerant match: any "...count <X>...minimum <Y>" phrasing, code-agnostic.
  const match = text.match(/count\s+(\d+)\s+is below required minimum\s+(\d+)/i);
  if (!match) return null;
  return { approved: Number(match[1]), required: Number(match[2]) };
};

/** Minimum images a bound TrainingDataset needs before LoRA training will run. */
export const MIN_DATASET_IMAGES = 2;

/**
 * True when a failed train call is the bridge's `dataset_too_small` (HTTP 400)
 * case: the bound dataset has fewer than {@link MIN_DATASET_IMAGES} images.
 */
const isDatasetTooSmall = (error: unknown): boolean =>
  /dataset_too_small/i.test(formatAxiosError(error, ''));

/**
 * Turns a failed build-dataset/train error into a clear, actionable message. The
 * persistent-dataset path returns `dataset_too_small` when the bound dataset has
 * too few images; the legacy approved-candidate path returns a "count X below
 * minimum Y" shortfall. Both get an actionable message; otherwise falls back to
 * {@link formatAxiosError}. `imageCount` (when known) lets the dataset_too_small
 * message name the exact count.
 */
export const formatTrainingThresholdError = (
  error: unknown,
  fallback = 'Request failed',
  imageCount?: number
): string => {
  if (isDatasetTooSmall(error)) {
    const count = typeof imageCount === 'number' ? imageCount : null;
    const countText = count == null ? 'too few' : `only ${count}`;
    const imageWord = count === 1 ? 'image' : 'images';
    return (
      `This dataset has ${countText} ${imageWord}; LoRA training needs at least ` +
      `${MIN_DATASET_IMAGES} — approve or generate more.`
    );
  }
  const shortfall = parseApprovedCandidateShortfall(error);
  if (shortfall) {
    return (
      `You have ${shortfall.approved} approved image${shortfall.approved === 1 ? '' : 's'}, ` +
      `but training needs at least ${shortfall.required}. ` +
      'Approve more, or generate additional images to extend the dataset.'
    );
  }
  return formatAxiosError(error, fallback);
};
